import { type Row } from './type';
import { type ExtraOptions } from '../types';
import { NULL_GROUPING } from 'common/visualizations/views/agGridReact/Constants';

// Constants
const TOP_LEVEL_GROUP = 0;

// Types
type ExpansionMap = Record<string, boolean>;

/**
 * Normalizes a value by converting to strings for consistent key matching.
 */
const normalizeValue = (value: string | boolean | number): string => {
  return String(value);
};

/**
 * Converts route array to JSON string key matching AG-Grid's groupNodeId format.
 */
const routeToKey = (route: string[]): string => {
  return JSON.stringify(route);
};

/**
 * Constructs hierarchical route path from row data.
 * Uses _groupKeys for pivot tables, builds from field values for regular tables.
 */
const buildGroupRoute = (row: Row, groupColumnIds: string[], groupLevel: number): string[] => {
  // For pivot tables, _groupKeys already contains the complete route
  if (row._groupKeys && Array.isArray(row._groupKeys)) {
    // Return the route up to the current level (inclusive)
    const result = row._groupKeys.slice(0, groupLevel + 1).map((key) => {
      // Convert 'null' back to '__null_grouping__' to match expansion keys
      if (key === 'null') {
        return NULL_GROUPING;
      }
      // Convert boolean values to strings for consistent matching
      return normalizeValue(key);
    });
    return result;
  }

  // For regular tables, build route from individual field values
  const route: string[] = [];
  for (let i = 0; i <= groupLevel && i < groupColumnIds.length; i++) {
    const fieldId = groupColumnIds[i];
    const fieldValue = row[fieldId];
    // Use NULL_GROUPING for null values to match AG-Grid's expansion state keys
    // Convert boolean values to strings for consistent matching with expansion map
    const normalizedValue = fieldValue != null ? normalizeValue(fieldValue) : NULL_GROUPING;
    route.push(normalizedValue);
  }
  return route;
};

/**
 * Pre-processes expansion map to avoid parsing JSON keys per row.
 * Explicit state lookups are O(1), descendant checks are O(mapSize).
 */
const buildExpansionLookup = (expandedGroupsMap: ExpansionMap, agGridOpenNodeLevel?: number) => {
  const explicitExpanded = new Set<string>();
  const explicitCollapsed = new Set<string>();
  const parsedRoutes = new Map<string, string[]>();

  // Pre-parse ALL expansion keys ONCE (not per row)
  for (const [key, isExpanded] of Object.entries(expandedGroupsMap)) {
    try {
      const route = JSON.parse(key) as string[];
      if (Array.isArray(route)) {
        // Normalize boolean values to strings for consistent matching
        const normalizedRoute = route.map(normalizeValue);
        const normalizedKey = JSON.stringify(normalizedRoute);

        parsedRoutes.set(normalizedKey, normalizedRoute);
        if (isExpanded) {
          explicitExpanded.add(normalizedKey);
        } else {
          explicitCollapsed.add(normalizedKey);
        }
      }
    } catch (e) {
      // Skip invalid JSON keys
    }
  }

  return {
    explicitExpanded,
    explicitCollapsed,
    parsedRoutes,
    agGridOpenNodeLevel,
  };
};

/**
 * Determines if a specific route should be expanded using pre-processed lookup data.
 *
 * **Expansion Priority Logic**:
 * 1. **Explicit state** (manual user action) - highest priority
 *    - If user manually expanded/collapsed this exact route, use that state
 * 2. **Parent expansion for children** - show expanded children
 *    - If any descendant is manually expanded, expand parent chain
 * 3. **Expand-all fallback** - automatic expansion
 *    - Use agGridOpenNodeLevel for routes not manually touched
 *
 * @param routeKey - JSON stringified route (e.g., '["2021","Q1"]')
 * @param route - Parsed route array (e.g., ['2021', 'Q1'])
 * @param lookup - Pre-processed expansion data from buildExpansionLookup()
 * @returns true if route should be expanded, false if collapsed
 */
const isRouteExpanded = (
  routeKey: string,
  route: string[],
  lookup: ReturnType<typeof buildExpansionLookup>,
): boolean => {
  // Priority 1: Explicit state (manual user action)
  if (lookup.explicitExpanded.has(routeKey)) {
    return true;
  }
  if (lookup.explicitCollapsed.has(routeKey)) {
    return false;
  }

  const groupLevel = route.length - 1;

  // Priority 2: Check for expanded descendants requiring parent expansion
  if (lookup.agGridOpenNodeLevel !== undefined && groupLevel >= lookup.agGridOpenNodeLevel) {
    // O(manual toggles) - acceptable since manual expansion maps are assumed to be typically small
    for (const [childKey, childRoute] of lookup.parsedRoutes) {
      if (lookup.explicitExpanded.has(childKey) && isDescendant(childRoute, route)) {
        return true;
      }
    }
  }

  // Priority 3: Expand-all level fallback
  return lookup.agGridOpenNodeLevel !== undefined && groupLevel < lookup.agGridOpenNodeLevel;
};

/**
 * Checks if childRoute is a descendant of parentRoute (longer and matching prefix).
 */
const isDescendant = (childRoute: string[], parentRoute: string[]): boolean => {
  return (
    childRoute.length > parentRoute.length && parentRoute.every((value, index) => childRoute[index] === value)
  );
};

/**
 * Checks if all ancestor routes up to maxLevel are expanded (early terminates on collapse).
 */
const areAncestorsExpanded = (
  route: string[],
  maxLevel: number,
  lookup: ReturnType<typeof buildExpansionLookup>,
): { expanded: boolean; blockedAtLevel?: number } => {
  for (let level = 1; level <= maxLevel; level++) {
    const ancestorRoute = route.slice(0, level);
    const routeKey = routeToKey(ancestorRoute);
    if (!isRouteExpanded(routeKey, ancestorRoute, lookup)) {
      return { expanded: false, blockedAtLevel: level };
    }
  }
  return { expanded: true };
};

/**
 * Convenience function to check if a group at the given route is expanded.
 * Uses the optimized lookup infrastructure for better performance.
 *
 * @param route - Route array (e.g., ['2021', 'Q1'])
 * @param lookup - Pre-processed expansion data from buildExpansionLookup()
 * @returns true if the group should be expanded, false if collapsed
 */
const isGroupExpanded = (route: string[], lookup: ReturnType<typeof buildExpansionLookup>): boolean => {
  const routeKey = routeToKey(route);
  return isRouteExpanded(routeKey, route, lookup);
};

/**
 * Filters table rows to show only those that should be visible based on group expansion state and marks collapsed groups.
 * Respects hierarchical grouping where collapsed groups hide all their children.
 * Collapsed groups are kept but marked with _isCollapsedGroup: true.
 */
export const filterRowsForExpansion = (
  rows: Row[],
  expandedGroupsMap?: ExpansionMap,
  extraOptions?: ExtraOptions,
): Row[] => {
  if (!expandedGroupsMap || Object.keys(expandedGroupsMap).length === 0) {
    // No expanded groups - show only top-level headers (collapsed view)
    return rows.filter((row) => {
      const isTopLevel = row._groupLevel === TOP_LEVEL_GROUP;
      if (row._rowType === 'header' && isTopLevel) {
        // Mark top-level headers as collapsed when no expansion state
        row._isCollapsedGroup = true;
        return true;
      }
      // Hide ALL subtotals when no expansion data (this function is only called for inherit mode)
      if (row._rowType === 'subTotal') {
        return false;
      }
      return row._rowType === 'total';
    });
  }

  const groupColumnIds = extraOptions?.groupColumnIds || [];
  const agGridOpenNodeLevel = extraOptions?.agGridOpenNodeLevel;

  // Pre-process expansion map for O(1) lookups
  const expansionLookup = buildExpansionLookup(expandedGroupsMap, agGridOpenNodeLevel);

  const filteredRows: Row[] = [];
  const groupRoute: string[] = [];

  for (const row of rows) {
    const rowType = row._rowType;
    const groupLevel = row._groupLevel || 0;

    // Always include total rows (grand totals)
    if (rowType === 'total') {
      filteredRows.push(row);
      continue;
    }

    // Update the group route based on the current row
    if (rowType === 'header') {
      // Build route from field values or _groupKeys (buildGroupRoute handles both cases)
      const newRoute = buildGroupRoute(row, groupColumnIds, groupLevel);
      groupRoute.splice(0, groupRoute.length, ...newRoute);

      // Check if this group is expanded
      const currentGroupRoute = groupRoute.slice(0, groupLevel + 1);
      const isExpanded = isGroupExpanded(currentGroupRoute, expansionLookup);

      // Always include top-level group headers
      if (groupLevel === TOP_LEVEL_GROUP) {
        row._isCollapsedGroup = !isExpanded;
        filteredRows.push(row);
        continue;
      }

      // For nested groups, check if ALL ancestors are expanded
      const { expanded: shouldShowHeader } = areAncestorsExpanded(groupRoute, groupLevel, expansionLookup);

      if (shouldShowHeader) {
        row._isCollapsedGroup = !isExpanded;
        filteredRows.push(row);
      }
      continue;
    }

    // Handle subtotal rows - they should be shown when their group is expanded
    if (rowType === 'subTotal') {
      // Check if ALL parent groups in the hierarchy are expanded
      const { expanded: shouldShowSubtotal } = areAncestorsExpanded(
        groupRoute,
        groupLevel + 1,
        expansionLookup,
      );

      if (shouldShowSubtotal) {
        filteredRows.push(row);
      }
      continue;
    }

    // For data rows, check if all parent groups are expanded
    // Data rows should have the current group route from the last header processed
    const { expanded: shouldShowDataRow } = areAncestorsExpanded(
      groupRoute,
      groupRoute.length,
      expansionLookup,
    );

    if (shouldShowDataRow) {
      filteredRows.push(row);
    }
  }

  return filteredRows;
};
